/**
 * Iter-193v — Mission Simulations (CEO-only).
 *
 * Two-pane layout:
 *  - LEFT: list of simulations with KPIs preview
 *  - RIGHT (selected): detail with lines table + status actions + PDF
 *
 * Status flow: DRAFT → SENT → WON / LOST / CANCELLED
 *   - WON simulations can be CONVERTED to a real Mission
 *   - DRAFT/SENT lines are editable, others are read-only
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ContactPicker from "@/components/ContactPicker";
import IconAction from "@/components/IconAction";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScheduledQuoteEmails, { toLocalInput, fmtWhen } from "@/components/ScheduledQuoteEmails";

import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Ban, Briefcase, CalendarClock, Check, ChevronLeft, FileX, ChevronRight, Download, ExternalLink, Eye, FileText, Loader2, Mail, Pencil, Plus, RefreshCw,
  Save, Send, Settings, Sparkles, Trash2, X, ArrowRight, ZoomIn, ZoomOut,
} from "lucide-react";
// Iter-195m — react-pdf renders the PDF as <canvas> via PDF.js, which works
// reliably on mobile browsers (Chrome Android / iOS Safari) that refuse to
// embed PDFs in iframes. CRA does not support `new URL(..., import.meta.url)`
// the same way Vite does, so we point the worker at the CDN-hosted file
// matching the installed pdfjs-dist version.
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
// DEVIS-2026-017 -> DEVIS-BAIM-2026-017.pdf (mirrors backend quote_pdf.quote_filename)
const quoteFileName = (n) => `${String(n).includes("DEVIS-") ? String(n).replace("DEVIS-", "DEVIS-BAIM-") : `BAIM-${n}`}.pdf`;

const fmt = (n) =>
  n == null ? "—" : Number(n).toLocaleString("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABEL = {
  DRAFT: "Brouillon", SENT: "Envoyée", WON: "Gagnée", LOST: "Perdue", CANCELLED: "Annulée",
};
const STATUS_TONE = {
  DRAFT: "bg-zinc-100 text-zinc-700",
  SENT: "bg-amber-100 text-amber-900",
  WON: "bg-emerald-100 text-emerald-900",
  LOST: "bg-red-100 text-red-800",
  CANCELLED: "bg-zinc-200 text-zinc-600",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase ${STATUS_TONE[status] || "bg-zinc-100"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function MarginPill({ pct, target }) {
  if (pct == null) return <span className="text-zinc-400 text-xs">—</span>;
  let tone = "bg-emerald-100 text-emerald-900";
  if (target != null) {
    if (pct < target - 5) tone = "bg-red-100 text-red-800";
    else if (pct < target) tone = "bg-amber-100 text-amber-900";
  } else if (pct < 0) tone = "bg-red-100 text-red-800";
  else if (pct < 20) tone = "bg-amber-100 text-amber-900";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[11px] ${tone}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

export default function Simulations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(searchParams.get("sim"));
  const retryId = searchParams.get("retry");
  useEffect(() => { const q = searchParams.get("sim"); if (q) setSelectedId(q); }, [searchParams]);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // Iter-229b — global default for the devis "Conditions générales" section.
  const [showConditionsDefault, setShowConditionsDefault] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/simulations${statusFilter ? `?status=${statusFilter}` : ""}`);
      setSims(r.data.simulations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const totalRevenue = useMemo(
    () => sims.reduce((s, x) => s + (x.kpis?.revenue || 0), 0),
    [sims]
  );

  if (user && user.role !== "CEO") {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto text-center" data-testid="simulations-forbidden">
        <Sparkles size={42} className="mx-auto text-zinc-400 mb-4" />
        <h1 className="font-heading text-xl font-bold tracking-tight">Simulations de missions</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Cette fonctionnalité est exclusivement réservée au CEO.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="simulations-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight inline-flex items-center gap-2">
            <Sparkles size={20} className="text-zinc-700" /> Simulations de missions
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Modélisez une mission potentielle (profils + collaborateurs, TJM, jours par phase)
            et obtenez une projection de rentabilité avant de proposer au client.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 border border-zinc-200 rounded px-2 text-sm bg-white"
            data-testid="sim-filter-status"
          >
            <option value="">Tous statuts</option>
            <option value="DRAFT">Brouillon</option>
            <option value="SENT">Envoyée</option>
            <option value="WON">Gagnée</option>
            <option value="LOST">Perdue</option>
            <option value="CANCELLED">Annulée</option>
          </select>
          <button
            onClick={() => setShowConditionsDefault(true)}
            data-testid="sim-quote-conditions-default-btn"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50"
            title="Texte par défaut des « Conditions générales » de tous les devis"
          >
            <FileText size={12} /> Conditions générales
          </button>
          <button
            onClick={load}
            data-testid="sim-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="sim-create-btn"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
          >
            <Plus size={14} /> Nouvelle simulation
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-2" data-testid="sim-list">
          {sims.length === 0 && !loading && (
            <div className="bg-white border border-dashed border-zinc-200 rounded-lg p-8 text-center text-zinc-500 text-sm">
              Aucune simulation. Cliquez sur <b>Nouvelle simulation</b> pour commencer.
            </div>
          )}
          {sims.map((s) => (
            <button
              key={s.simulation_id}
              onClick={() => setSelectedId(s.simulation_id)}
              data-testid={`sim-card-${s.simulation_id}`}
              className={`w-full text-left bg-white border rounded-lg p-3 hover:border-zinc-400 transition-colors ${selectedId === s.simulation_id ? "border-zinc-950 shadow-sm" : "border-zinc-200"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-sm truncate flex-1" title={s.title}>{s.title}</div>
                <StatusBadge status={s.status} />
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{s.client_name || "Sans client"}</div>
              <div className="flex items-center justify-between mt-2 text-[11px] font-mono">
                <div className="text-zinc-600">
                  CA <b className="text-zinc-900">{fmt(s.kpis?.revenue)}</b> {s.currency}
                </div>
                <MarginPill pct={s.kpis?.margin_pct} target={s.margin_target_pct} />
              </div>
            </button>
          ))}
          {sims.length > 0 && (
            <div className="text-[10px] text-zinc-500 px-1 mt-2 font-mono">
              {sims.length} simulation(s) · CA cumulé {fmt(totalRevenue)} MAD
            </div>
          )}
        </aside>

        <main className="lg:col-span-8" data-testid="sim-detail">
          {!selectedId ? (
            <div className="bg-white border border-dashed border-zinc-200 rounded-lg p-12 text-center text-zinc-500 text-sm">
              Sélectionnez une simulation à gauche, ou créez-en une nouvelle.
            </div>
          ) : (
            <SimulationDetail
              key={selectedId}
              simulationId={selectedId}
              highlightScheduleId={retryId}
              onChanged={load}
              onDeleted={() => { setSelectedId(null); load(); }}
              onConverted={(missionId) => { toast.success("Mission créée"); navigate(`/missions/${missionId}`); }}
            />
          )}
        </main>
      </div>

      <CreateSimulationDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(s) => {
          setShowCreate(false);
          setSelectedId(s.simulation_id);
          load();
        }}
      />
      <QuoteConditionsDefaultDialog
        open={showConditionsDefault}
        onClose={() => setShowConditionsDefault(false)}
      />
    </div>
  );
}

function CreateSimulationDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", client_id: "", client_name: "", client_address: "", client_ice: "",
    currency: "MAD", start_date: "", end_date: "", margin_target_pct: "",
    // Iter-198 — contact client persisté sur la simulation et propagé vers la mission
    // lors de la conversion. Civilité utilisée comme {{contact_gender}} dans les emails.
    contact_name: "", contact_email: "", contact_gender: "",
  });
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);        // Iter-291 — prospect base
  const [mode, setMode] = useState("client");            // client | prospect | new
  const [groups, setGroups] = useState([]);              // Iter-228k — group/holding filter
  const [groupFilter, setGroupFilter] = useState("");    // "" = all groups (no filter)
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => setClients([]));
    api.get("/prospects").then((r) => setProspects(r.data?.prospects || [])).catch(() => setProspects([]));
    api.get("/client-groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
  }, [open]);
  const applyContact = (c) => setForm((f) => ({
    ...f, contact_name: c?.contact_name || "", contact_email: c?.contact_email || "", contact_gender: c?.contact_gender || "",
  }));
  // Iter-228k — when a group is picked, only show clients of that group.
  const visibleClients = useMemo(() => {
    if (!groupFilter) return clients;
    const key = groupFilter.toLowerCase();
    return clients.filter((c) => (c.group_name || "").toLowerCase() === key);
  }, [clients, groupFilter]);
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    if (mode !== "new" && !form.client_id) {
      toast.error(mode === "client" ? "Choisissez un client existant" : "Choisissez un prospect existant");
      return;
    }
    if (mode === "new" && !form.client_name.trim()) { toast.error("Nom du prospect requis"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        client_id: form.client_id || null,
        client_name: form.client_id ? null : (form.client_name || null),
        client_address: form.client_id ? null : (form.client_address || null),
        client_ice: form.client_id ? null : (form.client_ice || null),
        description: "",
        currency: form.currency,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        margin_target_pct: form.margin_target_pct ? parseFloat(form.margin_target_pct) : null,
        // Iter-198 — contact info
        contact_name: form.contact_name || "",
        contact_email: form.contact_email || "",
        contact_gender: form.contact_gender || "",
      };
      const r = await api.post("/simulations", payload);
      onCreated?.(r.data);
      setMode("client");
      setForm({ title: "", client_id: "", client_name: "", client_address: "", client_ice: "", currency: "MAD", start_date: "", end_date: "", margin_target_pct: "", contact_name: "", contact_email: "", contact_gender: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec création");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sim-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle simulation</DialogTitle>
          <DialogDescription>Vous pourrez ajouter les lignes ressources × phase juste après.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Titre *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="POC Refonte BI" data-testid="sim-create-title" />
          </div>
          {/* Iter-291 — three ways to attach the simulation: existing client,
              existing prospect, or a brand-new prospect (created in the
              Prospects base and promoted to client when the mission is WON). */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Bénéficiaire</Label>
            <div className="grid grid-cols-3 gap-1 p-1 bg-zinc-100 rounded-md mt-1" data-testid="sim-create-target-mode">
              {[
                { k: "client", l: "Client existant" },
                { k: "prospect", l: "Prospect existant" },
                { k: "new", l: "Nouveau prospect" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  data-testid={`sim-create-mode-${o.k}`}
                  onClick={() => { setMode(o.k); setForm((f) => ({ ...f, client_id: "" })); }}
                  className={`h-8 rounded text-[11px] font-medium transition-colors ${mode === o.k ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500 hover:text-zinc-800"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          {mode === "client" && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Client existant</Label>
                <select
                  value={form.client_id}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({ ...form, client_id: v });
                    // Auto-set the group filter from the picked client's group.
                    const cur = clients.find((c) => c.client_id === v);
                    setGroupFilter(cur?.group_name || "");
                  }}
                  className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                  data-testid="sim-create-client"
                >
                  <option value="">— Choisir un client —</option>
                  {visibleClients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>{c.name}</option>
                  ))}
                </select>
                {groupFilter && visibleClients.length === 0 && (
                  <p className="text-[10px] text-zinc-500 mt-1 italic">Aucun client renseigné sur ce groupe pour le moment.</p>
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Groupe / Holding</Label>
                <select
                  value={groupFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setGroupFilter(v);
                    if (v && form.client_id) {
                      const cur = clients.find((c) => c.client_id === form.client_id);
                      if (!cur || (cur.group_name || "").toLowerCase() !== v.toLowerCase()) {
                        setForm((f) => ({ ...f, client_id: "" }));
                      }
                    }
                  }}
                  className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                  data-testid="sim-create-group-filter"
                >
                  <option value="">— Tous les groupes —</option>
                  {groups.map((g) => (
                    <option key={g.name} value={g.name}>{g.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          {mode === "prospect" && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Prospect existant</Label>
              <select
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                data-testid="sim-create-prospect"
              >
                <option value="">— Choisir un prospect —</option>
                {prospects.map((p) => (
                  <option key={p.client_id} value={p.client_id}>{p.name}{p.group_name ? ` · ${p.group_name}` : ""}</option>
                ))}
              </select>
              {prospects.length === 0 && (
                <p className="text-[10px] text-zinc-500 mt-1 italic">Aucun prospect enregistré — utilisez « Nouveau prospect ».</p>
              )}
            </div>
          )}
          {mode === "new" && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom du prospect *</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="AcmeCorp" data-testid="sim-create-client-name" />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Créé dans la base <strong>Prospects</strong> ; il basculera en client dès que la mission sera gagnée.
                </p>
              </div>
              {/* Iter-194e — Adresse + ICE persistés sur la simulation et rendus dans le bloc
                  "DEVIS POUR :" du PDF si pas de client_id existant. */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Adresse du prospect</Label>
                <Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="Rue, ville, pays" data-testid="sim-create-client-address" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">ICE</Label>
                <Input value={form.client_ice} onChange={(e) => setForm({ ...form, client_ice: e.target.value })} placeholder="Identifiant fiscal" data-testid="sim-create-client-ice" />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Devise</Label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
              >
                <option>MAD</option><option>EUR</option><option>USD</option><option>XOF</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Marge cible %</Label>
              <Input type="number" value={form.margin_target_pct} onChange={(e) => setForm({ ...form, margin_target_pct: e.target.value })} placeholder="40" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Début</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          {/* Iter-198 — contact client (carried over to mission on conversion). */}
          <div className="border-t border-zinc-200 pt-3 mt-1">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Contact client</div>
            <ContactPicker clientId={mode !== "new" ? form.client_id : ""} value={form} onApply={applyContact} autoApplyPrimary testidPrefix="sim-create-contact">
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Civilité</Label>
                <select
                  data-testid="sim-create-contact-gender"
                  value={form.contact_gender}
                  onChange={(e) => setForm({ ...form, contact_gender: e.target.value })}
                  className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                >
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme.">Mme.</option>
                  <option value="M./Mme">M./Mme</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom contact</Label>
                <Input data-testid="sim-create-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Jean Dupont" />
              </div>
            </div>
            <div className="mt-2">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email contact</Label>
              <Input data-testid="sim-create-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@client.com" />
            </div>
            </ContactPicker>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="sim-create-submit"
            className="px-3 h-9 bg-zinc-950 text-white text-sm rounded hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Créer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimulationDetail({ simulationId, highlightScheduleId, onChanged, onDeleted, onConverted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [lineEdit, setLineEdit] = useState(null);
  const [editHeader, setEditHeader] = useState(false);
  // Iter-195 — in-app PDF preview modal (no download).
  const [pdfPreview, setPdfPreview] = useState(null); // { url, quote_number }
  // Iter-228m — quote-by-email dialog + signature settings dialog.
  const [emailDialog, setEmailDialog] = useState(null);   // { open: true }
  const [schedKey, setSchedKey] = useState(0);
  const [sigSettings, setSigSettings] = useState(false);  // open the signature editor
  const [tplSettings, setTplSettings] = useState(false);  // Iter-228s — open the email template editor
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/simulations/${simulationId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [simulationId]);
  useEffect(() => {
    api.get("/simulations/_meta/collaborators").then((r) => setCollaborators(r.data?.collaborators || [])).catch(() => {});
  }, []);
  // Iter-195 — clean up the blob URL whenever the preview closes.
  useEffect(() => {
    return () => { if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url); };
  }, [pdfPreview?.url]);

  if (loading || !data) return (
    <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-zinc-500 text-sm">
      <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
    </div>
  );
  const { simulation: s, lines, kpis } = data;
  const editable = s.status === "DRAFT" || s.status === "SENT";

  const setStatus = async (next, extra) => {
    try {
      await api.patch(`/simulations/${simulationId}/status`, { status: next, ...extra });
      toast.success(`Statut mis à jour : ${STATUS_LABEL[next]}`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const issueQuote = async () => {
    // Iter-195 — Regeneration always allocates a new number and archives the
    // previous active devis into quote_history. Warn the CEO so they don't
    // accidentally burn a devis number.
    const isRegen = !!data.simulation.quote_number;
    if (isRegen) {
      const ok = confirm(
        `Régénérer le devis ?\n\nLe devis actuel (${data.simulation.quote_number}) sera archivé dans l'historique et un NOUVEAU numéro sera attribué (incrémenté à partir du dernier devis généré toutes simulations confondues).\n\nSi vous souhaitez seulement mettre à jour le PDF sans changer le numéro, supprimez d'abord le devis actuel.`
      );
      if (!ok) return;
    }
    try {
      const r = await api.post(`/simulations/${simulationId}/quote/issue`);
      if (r.data?.regenerated) {
        toast.success(`Nouveau devis ${r.data.quote_number} généré · ${r.data.archived_number} archivé`);
      } else {
        toast.success(`Devis ${r.data.quote_number} généré`);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  // Iter-194f — delete the existing devis (PDF + number) so the user can
  // re-issue a fresh one reflecting the latest simulation edits.
  const deleteQuote = async () => {
    if (!confirm(`Supprimer le devis ${data.simulation.quote_number} ? Le PDF actuel sera effacé et un nouveau numéro sera attribué lors de la prochaine génération.`)) return;
    try {
      await api.delete(`/simulations/${simulationId}/quote`);
      toast.success("Devis supprimé. Cliquez sur « Générer devis » pour en émettre un nouveau.");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const downloadQuote = async () => {
    try {
      // Iter-228r — cache-buster (same rationale as viewQuote).
      const r = await api.get(`/simulations/${simulationId}/quote.pdf?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = quoteFileName(s.quote_number || simulationId);
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Échec téléchargement"); }
  };
  // Iter-195 — fetch a PDF blob (active or historical) and open it in the
  // in-app preview modal. Falls back to a toast on failure (410 means the
  // archived file was purged from disk).
  const viewQuote = async (quoteNumber, isActive) => {
    try {
      // Iter-228r — append a cache-buster so every click forces a fresh
      // fetch (the URL `/quote.pdf` is otherwise identical across regens
      // and proxies/browsers happily serve a stale blob).
      const cacheBust = `?t=${Date.now()}`;
      const path = isActive
        ? `/simulations/${simulationId}/quote.pdf${cacheBust}`
        : `/simulations/${simulationId}/quote-history/${encodeURIComponent(quoteNumber)}.pdf${cacheBust}`;
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPdfPreview({ url, quote_number: quoteNumber });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'afficher ce devis");
    }
  };
  // Iter-195 — also download from preview.
  const downloadFromPreview = () => {
    if (!pdfPreview) return;
    const a = document.createElement("a");
    a.href = pdfPreview.url;
    a.download = quoteFileName(pdfPreview.quote_number);
    a.click();
  };
  const convertMission = async () => {
    if (!confirm("Convertir cette simulation en mission réelle (statut DRAFT) ?")) return;
    try {
      const r = await api.post(`/simulations/${simulationId}/convert-to-mission`);
      onConverted && onConverted(r.data.mission_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const deleteSim = async () => {
    if (!confirm("Supprimer définitivement cette simulation ?")) return;
    try {
      await api.delete(`/simulations/${simulationId}`);
      toast.success("Simulation supprimée");
      onDeleted && onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const deleteLine = async (lineId) => {
    if (!confirm("Supprimer cette ligne ?")) return;
    try {
      await api.delete(`/simulations/${simulationId}/lines/${lineId}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  return (
    <div className="space-y-4" data-testid={`sim-detail-${simulationId}`}>
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex-1 min-w-0" data-testid="sim-header-info">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-xl font-bold tracking-tight break-words" data-testid="sim-title">{s.title}</h2>
              <StatusBadge status={s.status} />
            </div>
            <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1 text-zinc-700" data-testid="sim-client-name">
                <Briefcase size={12} className="text-zinc-400" /> {s.client_name || "Sans client"}
              </span>
              {s.start_date && s.end_date && (
                <span className="inline-flex items-center gap-1 font-mono whitespace-nowrap" data-testid="sim-dates">
                  <CalendarClock size={12} className="text-zinc-400" /> {s.start_date} → {s.end_date}
                </span>
              )}
              {s.quote_number && (
                <span className="inline-flex items-center gap-1 font-mono whitespace-nowrap px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700" data-testid="sim-quote-number">
                  <FileText size={11} /> {s.quote_number}
                </span>
              )}
            </div>
            {s.description && <p className="text-xs text-zinc-600 mt-2">{s.description}</p>}
          </div>
          <TooltipProvider>
            <div className="flex items-center gap-1 flex-wrap md:justify-end shrink-0 md:max-w-[55%]" data-testid="sim-actions">
              {s.quote_number && (s.status === "DRAFT" || s.status === "SENT") && (
                <IconAction icon={Mail} tone="primary" testid="sim-send-quote-email" label="Envoyer le devis par email (immédiat ou programmé)" onClick={() => setEmailDialog({ open: true })} />
              )}
              {s.status === "DRAFT" && (
                <IconAction icon={Send} tone="warning" testid="sim-status-sent" label="Marquer comme envoyée" onClick={() => setStatus("SENT")} />
              )}
              {s.status === "SENT" && (
                <>
                  <IconAction icon={Check} tone="success" testid="sim-status-won" label="Marquer gagnée (convertit le prospect en client)" onClick={() => setStatus("WON")} />
                  <IconAction icon={X} tone="danger" testid="sim-status-lost" label="Marquer perdue" onClick={() => {
                    const reason = prompt("Raison de la perte (optionnel) :") || undefined;
                    setStatus("LOST", reason ? { lost_reason: reason } : {});
                  }} />
                </>
              )}
              {s.status === "WON" && !s.converted_to_mission_id && (
                <IconAction icon={ArrowRight} tone="dark" testid="sim-convert-mission" label="Convertir en mission" onClick={convertMission} />
              )}
              {s.converted_to_mission_id && (
                <IconAction icon={Briefcase} tone="emeraldSoft" testid="sim-converted-link" label="Voir la mission créée" href={`/missions/${s.converted_to_mission_id}`} />
              )}
              {s.status === "CANCELLED" && (
                <IconAction icon={RefreshCw} tone="dark" testid="sim-status-resume" label="Reprendre (repasse la simulation en brouillon)" onClick={() => setStatus("DRAFT")} />
              )}
              <span className="w-px h-5 bg-zinc-200 mx-0.5" aria-hidden />
              {editable && (
                <IconAction icon={Pencil} testid="sim-edit-header-btn" label="Modifier le client, l'adresse et l'ICE du devis" onClick={() => setEditHeader(true)} />
              )}
              {(s.status === "DRAFT" || s.status === "SENT") && (
                <IconAction icon={FileText} testid="sim-issue-quote" label={s.quote_number ? "Régénérer le devis" : "Générer le devis"} onClick={issueQuote} />
              )}
              {s.quote_number && (
                <>
                  <IconAction icon={Eye} testid="sim-view-quote" label="Aperçu du devis" onClick={() => viewQuote(s.quote_number, true)} />
                  <IconAction icon={Download} testid="sim-download-quote" label="Télécharger le devis PDF" onClick={downloadQuote} />
                </>
              )}
              {(s.status === "DRAFT" || s.status === "SENT" || (s.quote_number && editable) || !s.converted_to_mission_id) && (
                <span className="w-px h-5 bg-zinc-200 mx-0.5" aria-hidden />
              )}
              {(s.status === "DRAFT" || s.status === "SENT") && (
                <IconAction icon={Ban} testid="sim-status-cancel" label="Annuler la simulation" onClick={() => setStatus("CANCELLED")} />
              )}
              {s.quote_number && editable && (
                <IconAction icon={FileX} tone="dangerSoft" testid="sim-delete-quote" label="Supprimer le devis (pour en générer un nouveau avec un nouveau numéro)" onClick={deleteQuote} />
              )}
              {!s.converted_to_mission_id && (
                <IconAction icon={Trash2} tone="danger" testid="sim-delete" label="Supprimer la simulation" onClick={deleteSim} />
              )}
            </div>
          </TooltipProvider>
        </div>
        <ScheduledQuoteEmails simulationId={s.simulation_id} highlightId={highlightScheduleId} refreshKey={`${schedKey}-${s.updated_at}`} onChanged={load} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="sim-kpis">
        <KpiCard label="CA prévisionnel" value={`${fmt(kpis.revenue)} ${s.currency}`} accent="emerald" testid="kpi-revenue" />
        <KpiCard label="Coût interne" value={fmt(kpis.internal_cost)} testid="kpi-cost" />
        <KpiCard
          label="Marge"
          value={`${fmt(kpis.margin_abs)} (${kpis.margin_pct == null ? "—" : `${kpis.margin_pct.toFixed(1)}%`})`}
          accent={kpis.margin_pct != null && kpis.margin_pct >= (s.margin_target_pct || 20) ? "emerald" : (kpis.margin_pct != null && kpis.margin_pct >= 10 ? "amber" : "red")}
          testid="kpi-margin"
        />
        <KpiCard label="Jours / intervenants" value={`${kpis.total_days} j · ${kpis.headcount}`} testid="kpi-days" />
      </div>

      {/* Iter-195 — Devis history: active + archived quotes for this simulation.
          Iter-195c — passes current `kpis` so the active row reads live revenue/margin
          while archived rows display their snapshotted values. */}
      {(s.quote_number || (s.quote_history && s.quote_history.length > 0)) && (
        <QuoteHistoryPanel
          simulation={s}
          kpis={kpis}
          onView={viewQuote}
        />
      )}

      {/* Lines */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Lignes ressources × phase</div>
          {editable && (
            <button onClick={() => setLineEdit({ _isNew: true, phase_name: "Phase 1", resource_type: "profile", billing_mode: "regie", days: 1, tjm: 0, cost_per_day: 0 })}
              data-testid="sim-add-line"
              className="inline-flex items-center gap-1 px-2 h-7 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800">
              <Plus size={11} /> Ajouter une ligne
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                <th className="px-2 py-2">Phase</th>
                <th className="px-2 py-2">Ressource</th>
                <th className="px-2 py-2 text-right">Jours</th>
                <th className="px-2 py-2 text-right">TJM</th>
                <th className="px-2 py-2 text-right">Coût/j</th>
                <th className="px-2 py-2 text-right">CA</th>
                <th className="px-2 py-2 text-right">Marge</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr><td colSpan={8} className="px-2 py-12 text-center text-zinc-500 text-sm">
                  Aucune ligne. {editable && "Ajoutez votre première ressource (profil ou collaborateur)."}
                </td></tr>
              )}
              {lines.map((l) => {
                const ca = (Number(l.days) || 0) * (Number(l.tjm) || 0);
                const cost = (Number(l.days) || 0) * (Number(l.cost_per_day) || 0);
                const m = ca - cost;
                return (
                  <tr key={l.line_id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`sim-line-${l.line_id}`}>
                    <td className="px-2 py-2 text-xs">{l.phase_name}</td>
                    <td className="px-2 py-2 text-sm">
                      {l.resource_type === "collaborator" ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">{l.collaborator_name || "—"}</span>
                          <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-blue-100 text-blue-900">Collab</span>
                          {l.billing_mode === "forfait" && (
                            <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-amber-100 text-amber-900" data-testid={`sim-line-badge-forfait-${l.line_id}`}>Forfait</span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">{l.profile_label}</span>
                          <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-zinc-200 text-zinc-700">Profil</span>
                          {l.billing_mode === "forfait" && (
                            <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-amber-100 text-amber-900" data-testid={`sim-line-badge-forfait-${l.line_id}`}>Forfait</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs">{Number(l.days).toFixed(1)}</td>
                    <td className="px-2 py-2 text-right font-mono text-xs">{fmt(l.tjm)}</td>
                    <td className="px-2 py-2 text-right font-mono text-xs text-zinc-500">{l.cost_per_day == null ? "—" : fmt(l.cost_per_day)}</td>
                    <td className="px-2 py-2 text-right font-mono text-sm font-semibold">{fmt(ca)}</td>
                    <td className="px-2 py-2 text-right font-mono text-xs">{fmt(m)}</td>
                    <td className="px-2 py-2 text-right">
                      {editable ? (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setLineEdit({ ...l })} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded" data-testid={`sim-line-edit-${l.line_id}`}>
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => deleteLine(l.line_id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" data-testid={`sim-line-delete-${l.line_id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ) : <span className="text-zinc-300 text-[10px]">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {lines.length > 0 && (
              <tfoot className="bg-zinc-50 font-mono text-sm">
                <tr className="border-t border-zinc-200">
                  <td colSpan={5} className="px-2 py-2 text-right text-zinc-700 font-semibold">Total</td>
                  <td className="px-2 py-2 text-right font-bold">{fmt(kpis.revenue)} {s.currency}</td>
                  <td className="px-2 py-2 text-right">{fmt(kpis.margin_abs)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* By-phase breakdown */}
      {kpis.by_phase && kpis.by_phase.length > 1 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="sim-phase-breakdown">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-2">Récap par phase</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {kpis.by_phase.map((p) => (
              <div key={p.phase_name} className="border border-zinc-100 rounded p-2 text-xs">
                <div className="font-semibold">{p.phase_name}</div>
                <div className="font-mono text-zinc-600">{p.days} j · CA {fmt(p.revenue)}</div>
                <div className="font-mono text-[10px] text-zinc-400">Coût {fmt(p.cost)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lost reason */}
      {s.status === "LOST" && s.lost_reason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
          <b>Raison de la perte :</b> {s.lost_reason}
        </div>
      )}

      {/* Iter-229b — Conditions générales du devis (ex-N.B. du PDF). */}
      <QuoteConditionsSection
        key={s.simulation_id}
        sim={s}
        onSaved={async () => { await load(); onChanged && onChanged(); }}
      />

      <LineEditDialog
        editing={lineEdit}
        simulationId={simulationId}
        collaborators={collaborators}
        onClose={() => setLineEdit(null)}
        onSaved={async () => { setLineEdit(null); await load(); onChanged && onChanged(); }}
      />

      {/* Iter-194e — Edit header dialog : title / client (existing or free-text)
          / Address / ICE / Description / Currency / Dates / Margin target.
          Currency drives the DEVIS VAT (20% if MAD else Hors Taxe). */}
      <SimulationHeaderEditDialog
        open={editHeader}
        sim={s}
        onClose={() => setEditHeader(false)}
        onSaved={async () => { setEditHeader(false); await load(); onChanged && onChanged(); }}
      />

      {/* Iter-195 — Inline PDF preview modal (active or historical devis). */}
      <QuotePreviewDialog
        preview={pdfPreview}
        onClose={() => setPdfPreview(null)}
        onDownload={downloadFromPreview}
      />

      {/* Iter-228m — Email send dialog + dedicated signature editor. */}
      <SendQuoteEmailDialog
        open={!!emailDialog?.open}
        sim={s}
        kpis={kpis}
        onClose={() => setEmailDialog(null)}
        onSent={async () => { setEmailDialog(null); await load(); onChanged && onChanged(); }}
        onScheduled={() => { setEmailDialog(null); setSchedKey((k) => k + 1); }}
        onOpenSignatureSettings={() => setSigSettings(true)}
        onOpenTemplateSettings={() => setTplSettings(true)}
      />
      <SimulationQuoteSignatureDialog
        open={sigSettings}
        onClose={() => setSigSettings(false)}
      />
      <SimulationQuoteTemplateDialog
        open={tplSettings}
        onClose={() => setTplSettings(false)}
      />
    </div>
  );
}

// Iter-195 — Devis history panel : lists the currently active devis plus
// every archived version (chronological, newest first). Clicking a row opens
// the in-app preview modal (no download required).
// Iter-195c — each row now shows the snapshot KPIs (revenue HT, margin %)
// captured at archive time so the CEO can read the negotiation evolution at
// a glance. The active quote shows live KPIs from the current simulation.
function QuoteHistoryPanel({ simulation, kpis, onView }) {
  const history = (simulation.quote_history || []).slice().reverse();
  const items = [];
  if (simulation.quote_number) {
    items.push({
      quote_number: simulation.quote_number,
      generated_at: simulation.quote_generated_at,
      pdf_path: simulation.quote_pdf_path,
      drive_file_url: simulation.quote_drive_file_url,
      isActive: true,
      currency: simulation.currency || "MAD",
      revenue_ht: kpis?.revenue ?? null,
      margin_abs: kpis?.margin_abs ?? null,
      margin_pct: kpis?.margin_pct ?? null,
      total_days: kpis?.total_days ?? null,
      headcount: kpis?.headcount ?? null,
    });
  }
  for (const h of history) {
    items.push({ ...h, isActive: false });
  }
  if (items.length === 0) return null;
  // Compute delta vs the previous (older) version so the CEO sees the trend.
  // items are sorted newest-first; the "previous" for items[i] is items[i+1].
  const _toneFor = (delta) => {
    if (delta == null || delta === 0) return "text-zinc-400";
    return delta > 0 ? "text-emerald-600" : "text-red-600";
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="sim-quote-history">
      <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
          Devis générés ({items.length})
        </div>
        <div className="text-[10px] text-zinc-400">Cliquez pour visualiser</div>
      </div>
      <div className="divide-y divide-zinc-100">
        {items.map((q, idx) => {
          const prev = items[idx + 1];
          const revDelta = prev?.revenue_ht != null && q.revenue_ht != null
            ? q.revenue_ht - prev.revenue_ht : null;
          const marginDelta = prev?.margin_pct != null && q.margin_pct != null
            ? q.margin_pct - prev.margin_pct : null;
          return (
            <div
              key={q.quote_number + idx}
              role="button"
              tabIndex={0}
              onClick={() => onView(q.quote_number, q.isActive)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onView(q.quote_number, q.isActive); } }}
              data-testid={`sim-quote-history-item-${idx}`}
              className="w-full grid grid-cols-[1fr_auto] items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors cursor-pointer focus:outline-none focus:bg-zinc-50"
            >
              <div className="flex items-start gap-3 min-w-0">
                <FileText size={14} className={`mt-0.5 shrink-0 ${q.isActive ? "text-emerald-600" : "text-zinc-400"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold truncate">{q.quote_number}</span>
                    {q.isActive ? (
                      <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900">Actif</span>
                    ) : (
                      <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">Archivé</span>
                    )}
                    <span className="text-[10px] text-zinc-500 font-mono">
                      {q.generated_at ? new Date(q.generated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </span>
                  </div>
                  {/* Iter-195c — KPI snapshot row */}
                  <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] font-mono">
                    <span className="text-zinc-500">
                      CA HT&nbsp;
                      <span className="text-zinc-900 font-semibold">
                        {q.revenue_ht == null ? "—" : `${fmt(q.revenue_ht)} ${q.currency || "MAD"}`}
                      </span>
                      {revDelta != null && revDelta !== 0 && (
                        <span className={`ml-1 ${_toneFor(revDelta)}`} data-testid={`sim-quote-history-rev-delta-${idx}`}>
                          ({revDelta > 0 ? "+" : ""}{fmt(revDelta)})
                        </span>
                      )}
                    </span>
                    <span className="text-zinc-300">·</span>
                    <span className="text-zinc-500">
                      Marge&nbsp;
                      <span className="text-zinc-900 font-semibold">
                        {q.margin_pct == null ? "—" : `${Number(q.margin_pct).toFixed(1)}%`}
                      </span>
                      {marginDelta != null && Math.abs(marginDelta) > 0.05 && (
                        <span className={`ml-1 ${_toneFor(marginDelta)}`} data-testid={`sim-quote-history-margin-delta-${idx}`}>
                          ({marginDelta > 0 ? "+" : ""}{marginDelta.toFixed(1)}pt)
                        </span>
                      )}
                    </span>
                    {q.total_days != null && (
                      <>
                        <span className="text-zinc-300">·</span>
                        <span className="text-zinc-400">{Number(q.total_days).toFixed(0)} j</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Iter-228v-bis — "Voir sur Drive" deep-link. Stops the parent
                    row click so the user doesn't trigger the in-app preview. */}
                {q.drive_file_url && (
                  <a
                    href={q.drive_file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    data-testid={`sim-quote-history-drive-link-${idx}`}
                    title="Ouvrir ce devis dans Google Drive (nouvel onglet)"
                    className="inline-flex items-center gap-1 px-2 h-7 rounded text-[10px] font-mono uppercase tracking-wider text-zinc-600 border border-zinc-200 hover:bg-white hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                  >
                    <ExternalLink size={11} /> Drive
                  </a>
                )}
                <Eye size={14} className="text-zinc-400" />
              </div>
            </div>
          );
        })}
      </div>
      {items.length > 1 && (
        <div className="px-3 py-1.5 border-t border-zinc-100 bg-zinc-50/40 text-[10px] text-zinc-500 font-mono">
          Évolution depuis le premier devis émis · les écarts (vert/rouge) comparent chaque version à la précédente.
        </div>
      )}
    </div>
  );
}

// Iter-195m — In-app PDF preview modal using react-pdf (canvas rendering via
// PDF.js). Works on mobile browsers that refuse to render PDF iframes.
// Includes pagination, zoom controls, and responsive sizing.
function QuotePreviewDialog({ preview, onClose, onDownload }) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfError, setPdfError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Iter-195m — ref callback so we measure the container synchronously on
  // first paint (avoids a single empty frame where containerWidth is 0
  // and Page is conditionally skipped).
  const containerRefCb = (el) => {
    if (!el) return;
    setContainerWidth(el.clientWidth);
    // Observe future resizes (window resize, mobile rotation).
    if (!el.__roAttached) {
      const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
      ro.observe(el);
      el.__roAttached = true;
    }
  };

  // Reset state when a different devis is opened.
  useEffect(() => {
    setNumPages(0);
    setPageNumber(1);
    setScale(1.0);
    setPdfError(null);
  }, [preview?.quote_number]);

  if (!preview) return null;

  // Page width: container minus paddings (24px each side), clamped between 320 and 1100, scaled.
  const pageWidth = Math.min(1100, Math.max(320, containerWidth - 48)) * scale;

  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="sim-quote-preview-dialog"
        className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0"
      >
        <DialogHeader className="px-4 py-3 border-b border-zinc-100 flex-row items-center justify-between gap-3 space-y-0">
          <div className="min-w-0">
            <DialogTitle className="font-mono text-base truncate">{preview.quote_number}</DialogTitle>
            <DialogDescription className="text-xs">Aperçu du devis — pas de téléchargement obligatoire.</DialogDescription>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {numPages > 0 && (
              <>
                {/* Pagination */}
                <button
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                  data-testid="sim-quote-preview-prev"
                  className="inline-flex items-center justify-center h-8 w-8 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-30"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-[11px] font-mono text-zinc-600 px-1 select-none" data-testid="sim-quote-preview-pagenum">
                  {pageNumber}/{numPages}
                </span>
                <button
                  onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                  disabled={pageNumber >= numPages}
                  data-testid="sim-quote-preview-next"
                  className="inline-flex items-center justify-center h-8 w-8 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-30"
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
                {/* Zoom */}
                <span className="mx-1 h-5 w-px bg-zinc-200" aria-hidden />
                <button
                  onClick={() => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)))}
                  data-testid="sim-quote-preview-zoom-out"
                  className="inline-flex items-center justify-center h-8 w-8 border border-zinc-200 rounded hover:bg-zinc-50"
                  aria-label="Zoom out"
                >
                  <ZoomOut size={14} />
                </button>
                <span className="text-[11px] font-mono text-zinc-600 px-1 select-none w-10 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale((s) => Math.min(2.5, +(s + 0.15).toFixed(2)))}
                  data-testid="sim-quote-preview-zoom-in"
                  className="inline-flex items-center justify-center h-8 w-8 border border-zinc-200 rounded hover:bg-zinc-50"
                  aria-label="Zoom in"
                >
                  <ZoomIn size={14} />
                </button>
                <span className="mx-1 h-5 w-px bg-zinc-200" aria-hidden />
              </>
            )}
            <button
              onClick={onDownload}
              data-testid="sim-quote-preview-download"
              className="inline-flex items-center gap-1 px-3 h-8 border border-zinc-200 text-zinc-700 text-xs rounded hover:bg-zinc-50"
            >
              <Download size={12} /> <span className="hidden sm:inline">Télécharger</span>
            </button>
            <button
              onClick={onClose}
              data-testid="sim-quote-preview-close"
              className="inline-flex items-center justify-center h-8 w-8 border border-zinc-200 rounded hover:bg-zinc-50"
              aria-label="Close preview"
            >
              <X size={14} />
            </button>
          </div>
        </DialogHeader>
        <div
          ref={containerRefCb}
          className="flex-1 min-h-0 overflow-auto bg-zinc-100 p-6 flex justify-center items-start"
          data-testid="sim-quote-preview-canvas"
        >
          {pdfError ? (
            <div className="text-center max-w-md px-4 py-8">
              <FileText size={32} className="mx-auto text-zinc-400 mb-2" />
              <div className="text-sm text-zinc-700 mb-1">Aperçu indisponible</div>
              <div className="text-xs text-zinc-500 mb-3">{pdfError}</div>
              <button
                onClick={onDownload}
                className="inline-flex items-center gap-1 px-3 h-8 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800"
              >
                <Download size={12} /> Télécharger le PDF
              </button>
            </div>
          ) : (
            <Document
              file={preview.url}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(err) => setPdfError(err?.message || "Erreur de chargement du PDF")}
              loading={
                <div className="flex items-center gap-2 text-xs text-zinc-500 mt-6">
                  <Loader2 size={14} className="animate-spin" /> Chargement de l'aperçu…
                </div>
              }
              error={null /* handled by onLoadError → pdfError */}
            >
              {containerWidth > 0 && (
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mt-6">
                      <Loader2 size={14} className="animate-spin" /> Rendu de la page…
                    </div>
                  }
                  className="shadow-md"
                />
              )}
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SimulationHeaderEditDialog({ open, sim, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);        // Iter-291 — prospect base
  const [groups, setGroups] = useState([]);              // Iter-228k — group/holding filter
  const [groupFilter, setGroupFilter] = useState("");    // "" = all groups (no filter)
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !sim) return;
    api.get("/clients").then((r) => {
      const list = r.data || [];
      setClients(list);
      // Pre-select the group filter to the currently-attached client's group.
      const cur = list.find((c) => c.client_id === sim.client_id);
      setGroupFilter(cur?.group_name || "");
    }).catch(() => setClients([]));
    api.get("/prospects").then((r) => setProspects(r.data?.prospects || [])).catch(() => setProspects([]));
    api.get("/client-groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
    setForm({
      title: sim.title || "",
      client_id: sim.client_id || "",
      client_name: sim.client_name || "",
      client_address: sim.client_address || "",
      client_ice: sim.client_ice || "",
      description: sim.description || "",
      currency: sim.currency || "MAD",
      start_date: sim.start_date || "",
      end_date: sim.end_date || "",
      margin_target_pct: sim.margin_target_pct ?? "",
      // Iter-198 — contact client
      contact_name: sim.contact_name || "",
      contact_email: sim.contact_email || "",
      contact_gender: sim.contact_gender || "",
    });
  }, [open, sim]);

  // Iter-228k — filter the client picker by selected group/holding.
  const visibleClients = useMemo(() => {
    if (!groupFilter) return clients;
    const key = groupFilter.toLowerCase();
    return clients.filter((c) => (c.group_name || "").toLowerCase() === key);
  }, [clients, groupFilter]);

  if (!open || !form) return null;
  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        client_id: form.client_id || null,
        client_name: form.client_id ? null : (form.client_name || null),
        client_address: form.client_id ? null : (form.client_address || ""),
        client_ice: form.client_id ? null : (form.client_ice || ""),
        description: form.description,
        currency: form.currency,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        margin_target_pct: form.margin_target_pct === "" ? null : parseFloat(form.margin_target_pct),
        // Iter-198 — contact client editable post-creation
        contact_name: form.contact_name || "",
        contact_email: form.contact_email || "",
        contact_gender: form.contact_gender || "",
      };
      await api.patch(`/simulations/${sim.simulation_id}`, payload);
      toast.success("Simulation mise à jour");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sim-edit-header-dialog">
        <DialogHeader>
          <DialogTitle>Éditer la simulation</DialogTitle>
          <DialogDescription>L'adresse + ICE ci-dessous alimentent le bloc « DEVIS POUR » du PDF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Titre *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sim-edit-title" />
          </div>
          {/* Iter-228q — Order: Client picker FIRST, Group/Holding auto-fills below. */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Client / Prospect</Label>
            <select
              value={form.client_id}
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, client_id: v });
                const cur = clients.find((c) => c.client_id === v);
                setGroupFilter(cur?.group_name || "");
              }}
              className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
              data-testid="sim-edit-client"
            >
              <option value="">— Aucun (saisie libre) —</option>
              <optgroup label="Clients">
                {visibleClients.map((c) => (
                  <option key={c.client_id} value={c.client_id}>{c.name}</option>
                ))}
              </optgroup>
              <optgroup label="Prospects">
                {prospects.map((p) => (
                  <option key={p.client_id} value={p.client_id}>{p.name}</option>
                ))}
              </optgroup>
            </select>
            {groupFilter && visibleClients.length === 0 && (
              <p className="text-[10px] text-zinc-500 mt-1 italic">Aucun client renseigné sur ce groupe pour le moment.</p>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Groupe / Holding</Label>
            <select
              value={groupFilter}
              onChange={(e) => {
                const v = e.target.value;
                setGroupFilter(v);
                if (v && form.client_id) {
                  const cur = clients.find((c) => c.client_id === form.client_id);
                  if (!cur || (cur.group_name || "").toLowerCase() !== v.toLowerCase()) {
                    setForm((f) => ({ ...f, client_id: "" }));
                  }
                }
              }}
              className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
              data-testid="sim-edit-group-filter"
            >
              <option value="">— Tous les groupes —</option>
              {groups.map((g) => (
                <option key={g.name} value={g.name}>{g.name}</option>
              ))}
            </select>
          </div>
          {!form.client_id && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom du client (libre)</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="AcmeCorp" data-testid="sim-edit-client-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Adresse du client</Label>
                <Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="Rue, ville, pays" data-testid="sim-edit-client-address" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">ICE</Label>
                <Input value={form.client_ice} onChange={(e) => setForm({ ...form, client_ice: e.target.value })} placeholder="Identifiant fiscal" data-testid="sim-edit-client-ice" />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Devise</Label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                data-testid="sim-edit-currency"
              >
                <option>MAD</option><option>EUR</option><option>USD</option><option>XOF</option>
              </select>
              <div className="text-[10px] text-zinc-500 mt-1">
                {form.currency === "MAD" ? "TVA 20% appliquée au devis." : "Devis Hors Taxe (export)."}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Marge cible %</Label>
              <Input type="number" value={form.margin_target_pct} onChange={(e) => setForm({ ...form, margin_target_pct: e.target.value })} placeholder="40" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Début</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          {/* Iter-198 — contact client (édition). Civilité utilisée comme {{contact_gender}}. */}
          <div className="border-t border-zinc-200 pt-3 mt-1">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Contact client</div>
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Civilité</Label>
                <select
                  data-testid="sim-edit-contact-gender"
                  value={form.contact_gender}
                  onChange={(e) => setForm({ ...form, contact_gender: e.target.value })}
                  className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                >
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme.">Mme.</option>
                  <option value="M./Mme">M./Mme</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom contact</Label>
                <Input data-testid="sim-edit-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Jean Dupont" />
              </div>
            </div>
            <div className="mt-2">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email contact</Label>
              <Input data-testid="sim-edit-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@client.com" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="sim-edit-save"
            className="px-3 h-9 text-sm bg-zinc-950 text-white rounded hover:bg-zinc-800 disabled:opacity-50">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ label, value, accent, testid }) {
  const tone =
    accent === "emerald" ? "border-emerald-200 bg-emerald-50/60" :
    accent === "amber" ? "border-amber-200 bg-amber-50/60" :
    accent === "red" ? "border-red-200 bg-red-50/60" :
    "border-zinc-200 bg-white";
  return (
    <div className={`rounded-lg p-3 border ${tone}`} data-testid={testid}>
      <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="font-bold text-base font-mono mt-1">{value}</div>
    </div>
  );
}

function LineEditDialog({ editing, simulationId, collaborators, onClose, onSaved }) {
  const [form, setForm] = useState(editing || null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editing); }, [editing]);
  if (!editing || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (form.resource_type === "collaborator" && !form.collaborator_user_id) {
      toast.error("Sélectionnez un collaborateur"); return;
    }
    if (form.resource_type === "profile" && !(form.profile_label || "").trim()) {
      toast.error("Libellé du profil requis"); return;
    }
    setSaving(true);
    try {
      const payload = {
        phase_name: form.phase_name || "Phase 1",
        resource_type: form.resource_type,
        billing_mode: form.billing_mode || "regie",
        profile_label: form.resource_type === "profile" ? (form.profile_label || "").trim() : null,
        collaborator_user_id: form.resource_type === "collaborator" ? form.collaborator_user_id : null,
        days: Number(form.days || 0),
        tjm: Number(form.tjm || 0),
        cost_per_day: form.cost_per_day === "" || form.cost_per_day == null ? null : Number(form.cost_per_day),
        notes: form.notes || "",
        sort_order: form.sort_order || 0,
      };
      if (form._isNew) {
        await api.post(`/simulations/${simulationId}/lines`, payload);
      } else {
        await api.patch(`/simulations/${simulationId}/lines/${form.line_id}`, payload);
      }
      toast.success(form._isNew ? "Ligne ajoutée" : "Ligne mise à jour");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sim-line-dialog">
        <DialogHeader>
          <DialogTitle>{form._isNew ? "Nouvelle ligne" : "Modifier la ligne"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Phase</Label>
            <Input value={form.phase_name || ""} onChange={(e) => set("phase_name", e.target.value)} placeholder="Phase 1" data-testid="sim-line-phase" />
          </div>
          {/* Iter-228j — Régie / Forfait toggle. Labels of Jours / TJM
              switch automatically based on this mode. */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Mode de facturation</Label>
            <div className="flex border border-zinc-200 rounded overflow-hidden mt-1">
              <button type="button" onClick={() => set("billing_mode", "regie")} data-testid="sim-line-mode-regie"
                className={`flex-1 h-8 text-xs uppercase font-mono ${(form.billing_mode || "regie") === "regie" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}>
                Régie
              </button>
              <button type="button" onClick={() => set("billing_mode", "forfait")} data-testid="sim-line-mode-forfait"
                className={`flex-1 h-8 text-xs uppercase font-mono ${form.billing_mode === "forfait" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}>
                Forfait
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1 italic">
              {form.billing_mode === "forfait"
                ? "Forfait : la ligne du devis affichera « F » comme unité et le « Prix Total HT » = Unité × Prix unitaire."
                : "Régie : la ligne du devis affichera « Jours » comme unité et le TJM/J HT."}
            </p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Type de ressource</Label>
            <div className="flex border border-zinc-200 rounded overflow-hidden mt-1">
              <button type="button" onClick={() => set("resource_type", "profile")} data-testid="sim-line-type-profile"
                className={`flex-1 h-8 text-xs uppercase font-mono ${form.resource_type === "profile" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}>
                Profil
              </button>
              <button type="button" onClick={() => set("resource_type", "collaborator")} data-testid="sim-line-type-collab"
                className={`flex-1 h-8 text-xs uppercase font-mono ${form.resource_type === "collaborator" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}>
                Collaborateur
              </button>
            </div>
          </div>
          {form.resource_type === "collaborator" ? (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Collaborateur</Label>
              <select
                value={form.collaborator_user_id || ""}
                onChange={(e) => {
                  const uid = e.target.value;
                  const u = collaborators.find((c) => c.user_id === uid);
                  setForm((f) => ({ ...f, collaborator_user_id: uid, tjm: f.tjm || (u?.tjm_default || 0) }));
                }}
                className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white"
                data-testid="sim-line-collaborator"
              >
                <option value="">— Sélectionner —</option>
                {collaborators.map((c) => (
                  <option key={c.user_id} value={c.user_id}>{c.name} {c.tjm_default ? `(TJM ${c.tjm_default})` : ""}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Libellé du profil</Label>
              <Input value={form.profile_label || ""} onChange={(e) => set("profile_label", e.target.value)} placeholder="Senior Consultant" data-testid="sim-line-profile-label" />
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
                {form.billing_mode === "forfait" ? "Unités" : "Jours"}
              </Label>
              <Input type="number" step="0.5" min="0" value={form.days ?? ""} onChange={(e) => set("days", e.target.value)} data-testid="sim-line-days" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
                {form.billing_mode === "forfait" ? "Prix unitaire HT" : "TJM"}
              </Label>
              <Input type="number" step="0.01" min="0" value={form.tjm ?? ""} onChange={(e) => set("tjm", e.target.value)} data-testid="sim-line-tjm" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
                {form.billing_mode === "forfait" ? "Coût/U" : "Coût/j"}
              </Label>
              <Input type="number" step="0.01" min="0" value={form.cost_per_day ?? ""} onChange={(e) => set("cost_per_day", e.target.value)} placeholder="—" data-testid="sim-line-cost" />
            </div>
          </div>
          {(Number(form.days) || 0) > 0 && (Number(form.tjm) || 0) > 0 && (
            <div className="text-[11px] font-mono text-zinc-600 bg-zinc-50 rounded p-2">
              {form.billing_mode === "forfait" ? "Prix Total HT" : "Sous-total"} : <b>{fmt(Number(form.days) * Number(form.tjm))}</b> ·
              Marge : <b>{fmt((Number(form.days) * Number(form.tjm)) - (Number(form.days) * Number(form.cost_per_day || 0)))}</b>
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="sim-line-save"
            className="px-3 h-9 bg-zinc-950 text-white text-sm rounded hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {form._isNew ? "Ajouter" : "Sauvegarder"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter-228m — Dialog to send the active devis by email + dedicated CEO
// signature editor. Storage is independent of the Invoice email signature.
// ─────────────────────────────────────────────────────────────────────────
function _interp(tpl, vars) {
  if (!tpl) return "";
  return Object.entries(vars || {}).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, "g"), v == null ? "" : String(v)),
    tpl,
  );
}

function SendQuoteEmailDialog({ open, sim, kpis, onClose, onSent, onScheduled, onOpenSignatureSettings, onOpenTemplateSettings }) {
  const [to, setTo] = useState("");
  const [ccText, setCcText] = useState("");          // comma-separated
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");     // user-edited PLAIN TEXT (Iter-228s)
  const [signaturePreview, setSignaturePreview] = useState("");
  const [sending, setSending] = useState(false);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  // Format money for the template interpolation.
  const fmtMoney = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-MA", { minimumFractionDigits: 2 }));

  useEffect(() => {
    if (!open || !sim) return;
    (async () => {
      // 1) Pre-fill recipient from the simulation's contact_email OR the client's email.
      let initialTo = sim.contact_email || "";
      if (!initialTo && sim.client_id) {
        try {
          const cli = await api.get(`/clients/${sim.client_id}`).then((r) => r.data).catch(() => null);
          initialTo = cli?.email || "";
        } catch { /* ignore */ }
      }
      setTo(initialTo);
      setCcText("");
      setScheduleMode(false);
      setScheduleAt(toLocalInput());
      // 2) Load default subject/body templates from app_settings.
      const cfg = await api.get("/admin/simulation-quote-email/signature").then((r) => r.data).catch(() => ({}));
      const vatRate = (sim.currency || "MAD").toUpperCase() === "MAD" ? 0.2 : 0;
      const totalHt = kpis?.revenue || 0;
      const totalTtc = totalHt * (1 + vatRate);
      const vars = {
        quote_number: sim.quote_number || "",
        sim_title: sim.title || "",
        contact_name: sim.contact_name || (sim.contact_gender === "F" ? "Madame" : "Monsieur"),
        client_name: sim.client_name || "",
        total_ht_str: `${fmtMoney(totalHt)} ${sim.currency || "MAD"}`,
        total_ttc_str: `${fmtMoney(totalTtc)} ${sim.currency || "MAD"}`,
        total_days_str: fmtMoney(kpis?.total_days || 0),
      };
      setSubject(_interp(cfg.subject_template, vars));
      setBodyText(_interp(cfg.body_template, vars));
      // 3) Load signature preview (the saved version).
      try {
        const sig = await api.post("/admin/simulation-quote-email/signature/preview", {
          signature_html: cfg.signature_html || "",
          logo_position: cfg.logo_position || "inline-left",
        }).then((r) => r.data);
        setSignaturePreview(sig.signature_html || "");
      } catch { setSignaturePreview(""); }
      setDefaultsLoaded(true);
    })();
  }, [open, sim, kpis]);

  if (!open || !sim) return null;

  const send = async () => {
    if (!to.trim()) { toast.error("Destinataire requis"); return; }
    if (!subject.trim()) { toast.error("Sujet requis"); return; }
    setSending(true);
    try {
      const ccList = ccText.split(",").map((s) => s.trim()).filter(Boolean);
      if (scheduleMode) {
        const when = new Date(scheduleAt);
        if (!scheduleAt || Number.isNaN(when.getTime()) || when <= new Date()) {
          toast.error("Choisissez une date et une heure d'envoi dans le futur");
          return;
        }
        await api.post(`/simulations/${sim.simulation_id}/schedule-quote-email`, {
          to: to.trim(), cc: ccList, subject: subject.trim(), body_text: bodyText, scheduled_at: when.toISOString(),
        });
        toast.success(`Envoi du devis programmé le ${fmtWhen(when.toISOString())}`);
        onScheduled && onScheduled();
        return;
      }
      const r = await api.post(`/simulations/${sim.simulation_id}/send-quote-email`, {
        to: to.trim(),
        cc: ccList,
        subject: subject.trim(),
        body_text: bodyText,
      });
      toast.success(`Devis ${r.data?.quote_number} envoyé à ${to}`);
      onSent && onSent();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.code || "Échec de l'envoi"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="sim-send-quote-email-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle>Envoyer le devis par email</DialogTitle>
              <DialogDescription>
                Le devis actif <strong>{sim.quote_number || "—"}</strong> sera envoyé en pièce jointe via votre Gmail. Le bloc signature configuré ci-dessous est ajouté automatiquement.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={onOpenTemplateSettings}
              data-testid="sim-send-quote-email-edit-template"
              title="Configurer le template par défaut du corps du devis"
              className="shrink-0 inline-flex items-center gap-1 px-3 h-8 border border-zinc-200 text-zinc-700 text-xs rounded hover:bg-zinc-50"
            >
              <FileText size={12} /> Template
            </button>
            <button
              type="button"
              onClick={onOpenSignatureSettings}
              data-testid="sim-send-quote-email-edit-signature"
              title="Configurer la signature dédiée aux devis"
              className="shrink-0 inline-flex items-center gap-1 px-3 h-8 border border-zinc-200 text-zinc-700 text-xs rounded hover:bg-zinc-50"
            >
              <Settings size={12} /> Signature
            </button>
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">À *</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="prenom.nom@client.com" data-testid="sim-send-to" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">CC (séparés par virgules)</Label>
              <Input value={ccText} onChange={(e) => setCcText(e.target.value)} placeholder="manager@client.com, assistant@client.com" data-testid="sim-send-cc" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Sujet *</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="sim-send-subject" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Corps du message</Label>
              <Textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                className="text-sm min-h-[260px] leading-relaxed"
                data-testid="sim-send-body"
                placeholder="Écrivez votre message normalement. Les retours à la ligne sont conservés."
              />
              <p className="text-[10px] text-zinc-500 italic mt-1">
                Texte brut uniquement — seuls les retours à la ligne sont préservés. Le devis PDF actif est attaché automatiquement.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Aperçu du corps + signature</Label>
              <div className="border border-dashed border-zinc-300 rounded-md p-4 bg-zinc-50/50 min-h-[300px] overflow-auto" data-testid="sim-send-preview">
                <div className="whitespace-pre-wrap text-sm text-zinc-800 leading-relaxed">{bodyText}</div>
                {signaturePreview && (
                  <div className="mt-6" dangerouslySetInnerHTML={{ __html: signaturePreview }} />
                )}
                {!signaturePreview && defaultsLoaded && (
                  <p className="text-[11px] text-zinc-500 italic mt-4">
                    Pas de signature dédiée enregistrée — cliquez sur <strong>Signature</strong> en haut à droite pour en configurer une.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="border-t border-zinc-100 pt-4 sm:justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap" data-testid="sim-send-schedule-block">
            <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={scheduleMode} onChange={(e) => setScheduleMode(e.target.checked)} data-testid="sim-send-schedule-toggle" />
              <CalendarClock size={13} className="text-blue-600" /> Programmer l'envoi
            </label>
            {scheduleMode && (
              <input type="datetime-local" value={scheduleAt} min={toLocalInput(new Date().toISOString())}
                onChange={(e) => setScheduleAt(e.target.value)} data-testid="sim-send-schedule-at"
                className="h-9 border border-zinc-200 rounded px-2 text-xs bg-white" />
            )}
          </div>
          <div className="flex items-center gap-2">
          <button onClick={onClose} className="text-xs px-4 h-9 border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button onClick={send} disabled={sending || !defaultsLoaded} data-testid="sim-send-quote-email-confirm"
            className={`inline-flex items-center gap-2 px-4 h-9 text-white text-xs rounded disabled:opacity-50 transition-colors ${scheduleMode ? "bg-blue-600 hover:bg-blue-700" : "bg-zinc-950 hover:bg-zinc-800"}`}>
            {sending ? <Loader2 size={12} className="animate-spin" /> : (scheduleMode ? <CalendarClock size={12} /> : <Send size={12} />)}
            {sending ? (scheduleMode ? "Programmation…" : "Envoi…") : (scheduleMode ? "Programmer l'envoi" : "Envoyer le devis")}
          </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimulationQuoteSignatureDialog({ open, onClose }) {
  const [signatureHtml, setSignatureHtml] = useState("");
  const [savedSignature, setSavedSignature] = useState("");
  const [logoPosition, setLogoPosition] = useState("inline-left");
  const [savedLogoPosition, setSavedLogoPosition] = useState("inline-left");
  const [previewHtml, setPreviewHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const isDirty = signatureHtml !== savedSignature || logoPosition !== savedLogoPosition;

  const exec = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    if (editorRef.current) setSignatureHtml(editorRef.current.innerHTML);
  };

  const refreshPreview = async () => {
    try {
      const r = await api.post("/admin/simulation-quote-email/signature/preview", {
        signature_html: signatureHtml,
        logo_position: logoPosition,
      });
      setPreviewHtml(r.data?.signature_html || "");
    } catch { toast.error("Aperçu indisponible"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/simulation-quote-email/signature", {
        signature_html: signatureHtml,
        logo_position: logoPosition,
      });
      setSavedSignature(r.data?.signature_html || "");
      setSavedLogoPosition(r.data?.logo_position || logoPosition);
      toast.success("Signature enregistrée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const cfg = await api.get("/admin/simulation-quote-email/signature").then((r) => r.data).catch(() => ({}));
      const html = cfg.signature_html || "";
      const pos = cfg.logo_position || "inline-left";
      setSavedSignature(html);
      setSavedLogoPosition(pos);
      setSignatureHtml(html);
      setLogoPosition(pos);
      if (editorRef.current) editorRef.current.innerHTML = html;
      try {
        const sig = await api.post("/admin/simulation-quote-email/signature/preview", {
          signature_html: html, logo_position: pos,
        });
        setPreviewHtml(sig.data?.signature_html || "");
      } catch { setPreviewHtml(""); }
    })();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="sim-quote-signature-dialog">
        <DialogHeader>
          <DialogTitle>Signature dédiée aux envois de devis</DialogTitle>
          <DialogDescription>
            Personnalisez la signature ajoutée automatiquement aux emails envoyés depuis l&apos;espace simulation. <strong>Indépendante de la signature des factures.</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Position du logo</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {[{ v:"top",l:"Top" },{ v:"inline-left",l:"Inline (left)" },{ v:"bottom",l:"Bottom" },{ v:"none",l:"No logo" }].map((opt) => (
                  <button key={opt.v} type="button" onClick={() => setLogoPosition(opt.v)}
                    data-testid={`sim-sig-logo-pos-${opt.v}`}
                    className={`px-3 h-9 rounded-md text-xs font-medium transition-colors ${
                      logoPosition === opt.v ? "bg-brand-navy text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                    }`}>{opt.l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Contenu de la signature</Label>
              <div className="border border-zinc-200 rounded-md overflow-hidden mt-1">
                <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-zinc-100 bg-zinc-50">
                  <button type="button" onClick={() => exec("bold")} data-testid="sim-sig-btn-bold" className="w-8 h-8 rounded hover:bg-zinc-200 font-bold text-sm">B</button>
                  <button type="button" onClick={() => exec("italic")} data-testid="sim-sig-btn-italic" className="w-8 h-8 rounded hover:bg-zinc-200 italic text-sm">I</button>
                  <button type="button" onClick={() => exec("underline")} data-testid="sim-sig-btn-underline" className="w-8 h-8 rounded hover:bg-zinc-200 underline text-sm">U</button>
                  <span className="w-px h-5 bg-zinc-300 mx-1" />
                  <label className="inline-flex items-center gap-1 text-[10px] uppercase font-mono text-zinc-500 cursor-pointer">
                    <span>Color</span>
                    <input type="color" defaultValue="#1e5db4" onChange={(e) => exec("foreColor", e.target.value)} data-testid="sim-sig-btn-color" className="w-6 h-6 rounded border border-zinc-200 cursor-pointer" />
                  </label>
                  <span className="w-px h-5 bg-zinc-300 mx-1" />
                  <button type="button" onClick={() => { const u = window.prompt("Link URL", "https://"); if (u) exec("createLink", u); }} data-testid="sim-sig-btn-link" className="px-2 h-8 rounded hover:bg-zinc-200 text-xs">Link</button>
                  <button type="button" onClick={() => exec("removeFormat")} data-testid="sim-sig-btn-clear" className="px-2 h-8 rounded hover:bg-zinc-200 text-xs text-zinc-500">Clear</button>
                </div>
                <div ref={editorRef} contentEditable suppressContentEditableWarning
                  data-testid="sim-sig-editor"
                  onInput={(e) => setSignatureHtml(e.currentTarget.innerHTML)}
                  onBlur={(e) => setSignatureHtml(e.currentTarget.innerHTML)}
                  className="min-h-[180px] p-3 text-sm focus:outline-none"
                  style={{ fontFamily: "Verdana, Geneva, sans-serif" }} />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Aperçu</Label>
              <button type="button" onClick={refreshPreview} data-testid="sim-sig-preview-refresh"
                className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider rounded-md hover:bg-zinc-50">
                Apply &amp; preview
              </button>
            </div>
            <div className="border border-dashed border-zinc-300 rounded-md p-4 bg-zinc-50/50 min-h-[300px]"
              data-testid="sim-sig-preview"
              dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
        <DialogFooter className="border-t border-zinc-100 pt-4">
          <button onClick={onClose} className="text-xs px-4 h-9 border border-zinc-200 rounded hover:bg-zinc-50">Fermer</button>
          <button onClick={save} disabled={saving || !isDirty} data-testid="sim-sig-save"
            className="inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800 disabled:opacity-50">
            <Save size={12} /> {saving ? "Saving…" : "Save signature"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter-228s — "Template devis" editor : lets the CEO customise the default
// subject + body used when sending a devis by email. Plain-text only —
// only newlines are preserved as formatting. Variables are listed on the
// right-hand side with click-to-insert into the body.
// ─────────────────────────────────────────────────────────────────────────
function SimulationQuoteTemplateDialog({ open, onClose }) {
  const [subjectTpl, setSubjectTpl] = useState("");
  const [bodyTpl, setBodyTpl] = useState("");
  const [savedSubject, setSavedSubject] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [defaultSubject, setDefaultSubject] = useState("");
  const [defaultBody, setDefaultBody] = useState("");
  const [variables, setVariables] = useState([]);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);
  const isDirty = subjectTpl !== savedSubject || bodyTpl !== savedBody;

  useEffect(() => {
    if (!open) return;
    (async () => {
      const cfg = await api.get("/admin/simulation-quote-email/template")
        .then((r) => r.data).catch(() => ({}));
      setSubjectTpl(cfg.subject_template || "");
      setBodyTpl(cfg.body_template || "");
      setSavedSubject(cfg.subject_template || "");
      setSavedBody(cfg.body_template || "");
      setDefaultSubject(cfg.default_subject_template || "");
      setDefaultBody(cfg.default_body_template || "");
      setVariables(cfg.variables || []);
    })();
  }, [open]);

  const insertVar = (key) => {
    const token = `{${key}}`;
    const ta = bodyRef.current;
    if (!ta) { setBodyTpl((s) => s + token); return; }
    const start = ta.selectionStart ?? bodyTpl.length;
    const end = ta.selectionEnd ?? bodyTpl.length;
    const next = bodyTpl.slice(0, start) + token + bodyTpl.slice(end);
    setBodyTpl(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const resetToDefault = () => {
    setSubjectTpl(defaultSubject);
    setBodyTpl(defaultBody);
  };

  const save = async () => {
    if (!subjectTpl.trim()) { toast.error("Sujet requis"); return; }
    if (!bodyTpl.trim()) { toast.error("Corps requis"); return; }
    setSaving(true);
    try {
      const r = await api.put("/admin/simulation-quote-email/template", {
        subject_template: subjectTpl,
        body_template: bodyTpl,
      });
      setSavedSubject(r.data?.subject_template || subjectTpl);
      setSavedBody(r.data?.body_template || bodyTpl);
      toast.success("Template enregistré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="sim-quote-template-dialog">
        <DialogHeader>
          <DialogTitle>Template par défaut — Email d&apos;envoi de devis</DialogTitle>
          <DialogDescription>
            Configurez le sujet et le corps utilisés à l&apos;ouverture du dialogue d&apos;envoi. Texte brut uniquement,
            seuls les retours à la ligne sont conservés. Utilisez les variables ci-contre pour insérer dynamiquement
            les informations du devis.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-3 lg:col-span-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Sujet du mail</Label>
              <Input
                value={subjectTpl}
                onChange={(e) => setSubjectTpl(e.target.value)}
                data-testid="sim-tpl-subject"
                placeholder="Devis {quote_number} — {sim_title}"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Corps du mail</Label>
              <Textarea
                ref={bodyRef}
                value={bodyTpl}
                onChange={(e) => setBodyTpl(e.target.value)}
                className="text-sm min-h-[320px] leading-relaxed"
                data-testid="sim-tpl-body"
                placeholder={"Bonjour {contact_name},\n\nSuite à nos échanges..."}
              />
              <p className="text-[10px] text-zinc-500 italic mt-1">
                Texte brut. Une ligne vide entre 2 blocs crée un nouveau paragraphe dans le mail final.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Variables disponibles</Label>
            <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-[380px] overflow-auto">
              {variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVar(v.key)}
                  data-testid={`sim-tpl-var-${v.key}`}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-50 group"
                  title={`Insérer {${v.key}}`}
                >
                  <div className="font-mono text-[11px] text-brand-navy group-hover:text-brand-amber">
                    {`{${v.key}}`}
                  </div>
                  <div className="text-[11px] text-zinc-600">{v.label}</div>
                  {v.example && (
                    <div className="text-[10px] text-zinc-400 italic">ex. {v.example}</div>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={resetToDefault}
              data-testid="sim-tpl-reset"
              className="w-full mt-2 px-3 h-8 border border-zinc-200 text-[11px] text-zinc-600 rounded hover:bg-zinc-50"
            >
              Réinitialiser au template par défaut
            </button>
          </div>
        </div>
        <DialogFooter className="border-t border-zinc-100 pt-4">
          <button onClick={onClose} className="text-xs px-4 h-9 border border-zinc-200 rounded hover:bg-zinc-50">Fermer</button>
          <button onClick={save} disabled={saving || !isDirty} data-testid="sim-tpl-save"
            className="inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800 disabled:opacity-50">
            <Save size={12} /> {saving ? "Saving…" : "Enregistrer le template"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter-229b — "Conditions générales" du devis (ex-"N.B." dans le PDF).
// Per-simulation override; when empty the global default (or the hardcoded
// text) is rendered. Editable while the simulation is DRAFT/SENT.
function QuoteConditionsSection({ sim, onSaved }) {
  const editable = sim.status === "DRAFT" || sim.status === "SENT";
  const [text, setText] = useState(sim.quote_conditions_text || "");
  const [globalDefault, setGlobalDefault] = useState("");
  const [saving, setSaving] = useState(false);
  const dirty = (text || "") !== (sim.quote_conditions_text || "");

  useEffect(() => {
    api.get("/simulations/_meta/quote-conditions-default")
      .then((r) => setGlobalDefault(r.data?.text || ""))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/simulations/${sim.simulation_id}`, { quote_conditions_text: text });
      toast.success("Conditions générales enregistrées — régénérez le devis pour mettre à jour le PDF.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="sim-quote-conditions">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
          Conditions générales du devis
        </div>
        <span className="text-[10px] font-mono text-zinc-400">{(text || "").length}/1200</span>
      </div>
      <Textarea
        data-testid="sim-quote-conditions-text"
        value={text}
        maxLength={1200}
        rows={3}
        disabled={!editable}
        placeholder={globalDefault ? `Défaut global : ${globalDefault}` : "Texte des conditions générales du PDF…"}
        onChange={(e) => setText(e.target.value)}
        className="text-xs"
      />
      <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
        <p className="text-[10px] text-zinc-500 leading-relaxed max-w-md">
          Rendu dans la section « Conditions générales » du PDF (limité pour ne jamais empiéter sur le cachet).
          Vide = texte par défaut global (bouton « Conditions générales » en haut de page).
        </p>
        {editable && (
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="sim-quote-conditions-save"
            className="inline-flex items-center gap-1.5 px-3 h-8 bg-zinc-950 text-white text-xs rounded-md hover:bg-zinc-800 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Enregistrer
          </button>
        )}
      </div>
    </div>
  );
}

// Iter-229b — global default editor (applies to every devis without a
// per-simulation override).
function QuoteConditionsDefaultDialog({ open, onClose }) {
  const [text, setText] = useState("");
  const [hardcoded, setHardcoded] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/simulations/_meta/quote-conditions-default")
      .then((r) => { setText(r.data?.text || ""); setHardcoded(r.data?.hardcoded_default || ""); })
      .catch(() => {});
  }, [open]);

  const save = async (value) => {
    setSaving(true);
    try {
      const r = await api.put("/simulations/_meta/quote-conditions-default", { text: value });
      setText(r.data?.text || "");
      toast.success("Texte par défaut enregistré — s'applique aux prochains devis générés.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="sim-quote-conditions-default-dialog">
        <DialogHeader>
          <DialogTitle>Conditions générales par défaut (devis)</DialogTitle>
          <DialogDescription className="text-xs">
            Texte rendu dans la section « Conditions générales » de chaque devis PDF,
            sauf si la simulation définit son propre texte. Max 1200 caractères.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="sim-quote-conditions-default-text"
          value={text}
          maxLength={1200}
          rows={6}
          onChange={(e) => setText(e.target.value)}
          className="text-xs"
        />
        <div className="text-[10px] font-mono text-zinc-400 text-right">{(text || "").length}/1200</div>
        <DialogFooter className="gap-2">
          <button
            onClick={() => setText(hardcoded)}
            data-testid="sim-quote-conditions-default-reset"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50"
          >
            Rétablir le texte d'origine
          </button>
          <button
            onClick={() => save(text)}
            disabled={saving || !text.trim()}
            data-testid="sim-quote-conditions-default-save"
            className="inline-flex items-center gap-1.5 px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
